// @ts-nocheck
/*
 * Port of views/dashboard/user-wise-campaign-summary.js.
 * API: API_DASHBOARD_USER_WISE_CAMPAIGN_SUMMARY_STATS.
 * Column count preserved: 8 (User ID, Username, Total Campaigns, Total Numbers,
 * Calls Dialed, DnD Count, Connected Calls, Pending Calls).
 * DataTable handles sort / search (userId + username via searchColumnId) /
 * pagination / CSV export.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Search, X } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_DASHBOARD_USER_WISE_CAMPAIGN_SUMMARY_STATS } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

export default function UserWiseCampaignSummary() {
  const today = new Date().toISOString().split('T')[0];
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ startDate: today, endDate: today, userId: '' });
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return null;
      try {
        const user = JSON.parse(rawUser);
        if (user.role) setLoggedInUserRole(user.role);
      } catch (e) {}
    }
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole == USER_ROLES.CALL_CENTRE_AGENT) return;
    fetchData();

  }, [loggedInUserRole]);

  const fetchData = () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error('Start and End date required');
      return;
    }
    setLoading(true);
    let query = `${API_DASHBOARD_USER_WISE_CAMPAIGN_SUMMARY_STATS}?startDate=${filters.startDate}&endDate=${filters.endDate}`;
    if (filters.userId) query += `&userId=${filters.userId}`;
    getApiCall(query)
      .then((resp) => {
        const formatted = (resp.data || resp).map((item) => ({
          ...item,
          userId: item.userId?.toString(),
          username: item.username?.toString(),
        }));
        setData(formatted);
      })
      .catch((err) => toast.error(friendlyApiError(err) || 'Failed to fetch campaign summary'))
      .finally(() => setLoading(false));
  };

  const clearFilters = () => {
    setFilters({ startDate: today, endDate: today, userId: '' });
    fetchData();
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'userId', header: 'User ID' },
      { accessorKey: 'username', header: 'Username' },
      { accessorKey: 'totalCampaigns', header: 'Total Campaigns' },
      { accessorKey: 'totalNumbers', header: 'Total Numbers' },
      { accessorKey: 'callsDialed', header: 'Calls Dialed' },
      { accessorKey: 'dndCount', header: 'DnD Count' },
      { accessorKey: 'connectedCalls', header: 'Connected Calls' },
      { accessorKey: 'pendingCalls', header: 'Pending Calls' },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3 text-sm font-bold text-foreground">User-Wise Campaign Summary</div>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Start Date
          </label>
          <CalendarDateInput
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e })}
            className="h-9 w-40"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            End Date
          </label>
          <CalendarDateInput
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e })}
            className="h-9 w-40"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            User ID
          </label>
          <Input
            type="text"
            placeholder="Enter User ID"
            value={filters.userId}
            onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
            className="h-9 w-40"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={fetchData} title="Search">
            <Search className="mr-1.5 h-3.5 w-3.5" />Search
          </Button>
          <Button size="sm" variant="outline" onClick={clearFilters} title="Clear">
            <X className="mr-1.5 h-3.5 w-3.5" />Clear
          </Button>
        </div>
      </div>

      <DataTable
        tableId="dashboard.user-wise-campaign-summary"
        columns={columns}
        data={data}
        isLoading={loading}
        searchPlaceholder="Search by username or user ID…"
        emptyTitle="No data"
        emptyDescription="No data available for the selected date range."
        pageSize={10}
        exportFilename="user-wise-campaign-summary.csv"
      />
    </>
  );
}
